import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Table, type Column } from '@/components/ui/Table';
import { cn, getNameInitials } from '@/lib/utils';
import type { TransportStudent } from '@/types/transport';
import { ROUTE_TONE, STUDENT_TRANSPORT_STATUS, toClockLabel } from '../constants';
import { ROWS_PER_PAGE, useRegister } from '../useRegister';
import { RegisterToolbar } from './RegisterToolbar';
import { RowActions } from './RowActions';

/** What a status in the address may be — every one this register's rows can hold. */
const STUDENT_STATUSES = Object.keys(STUDENT_TRANSPORT_STATUS);

export interface StudentsTabProps {
  students: TransportStudent[];
  onOpenStudent?: (student: TransportStudent) => void;
  onEditStudent?: (student: TransportStudent) => void;
  onStudentMenu?: (student: TransportStudent) => void;
  /** Given the children ticked, or all of them when none are. */
  onExport?: (students: TransportStudent[]) => void;
}

/**
 * Every child on transport — and the ones who are not, which is the same
 * question asked the other way.
 *
 * Rows can be ticked, unlike the other registers. Transport is where an office
 * acts on *several* children at once — a stop moves, a route is suspended, a
 * term's passes are printed — and doing that one row at a time is the thing the
 * screen exists to avoid. Nothing destructive hangs off the selection; it feeds
 * Export, and the actions that follow it when the endpoints land.
 */
export function StudentsTab({
  students,
  onOpenStudent,
  onEditStudent,
  onStudentMenu,
  onExport,
}: StudentsTabProps) {
  const [selected, setSelected] = useState<string[]>([]);

  const register = useRegister({
    prefix: 'students',
    statuses: STUDENT_STATUSES,
    rows: students,
    fieldsOf: (student) => [
      student.fullName,
      student.studentCode,
      student.admissionNumber,
      student.className,
      student.routeName,
      student.stopName,
    ],
    statusOf: (student) => student.status,
  });

  const pageUuids = useMemo(() => register.page.map((student) => student.uuid), [register.page]);
  const allOnPageChosen = pageUuids.length > 0 && pageUuids.every((uuid) => selected.includes(uuid));
  const someOnPageChosen = !allOnPageChosen && pageUuids.some((uuid) => selected.includes(uuid));

  /**
   * The header tick covers this page only.
   *
   * Ticking it must never quietly select 245 children the reader cannot see —
   * the count beside Export says how many are actually chosen, and it has to
   * mean what it says.
   */
  function togglePage() {
    setSelected((current) =>
      allOnPageChosen
        ? current.filter((uuid) => !pageUuids.includes(uuid))
        : [...new Set([...current, ...pageUuids])],
    );
  }

  function toggleRow(uuid: string) {
    setSelected((current) =>
      current.includes(uuid) ? current.filter((item) => item !== uuid) : [...current, uuid],
    );
  }

  const chosen = students.filter((student) => selected.includes(student.uuid));

  const columns: Column<TransportStudent>[] = [
    {
      key: 'pick',
      header: (
        <Checkbox
          label="Select every student on this page"
          hideLabel
          checked={allOnPageChosen}
          indeterminate={someOnPageChosen}
          onChange={togglePage}
        />
      ),
      className: 'w-10',
      headerClassName: 'w-10',
      render: (student) => (
        <Checkbox
          label={`Select ${student.fullName}`}
          hideLabel
          checked={selected.includes(student.uuid)}
          onChange={() => toggleRow(student.uuid)}
        />
      ),
    },
    {
      key: 'student',
      header: 'Student name',
      render: (student) => (
        <div className="flex items-center gap-3">
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-[11px] font-black text-white shadow-sm"
            aria-hidden
          >
            {getNameInitials(student.fullName)}
          </span>
          <div className="min-w-0">
            <p className="truncate font-bold text-ink">{student.fullName}</p>
            <p className="truncate text-xs font-semibold text-ink-3">ID: {student.studentCode}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'class',
      header: 'Class & section',
      render: (student) => (
        <span className="font-semibold text-ink-2">
          {student.className} - {student.sectionName}
        </span>
      ),
    },
    {
      key: 'route',
      header: 'Route',
      render: (student) =>
        student.routeCode ? (
          <span
            className={cn(
              'pill text-[12px] font-bold tracking-label',
              ROUTE_TONE[student.routeTone ?? 'sky'].soft,
            )}
          >
            {student.routeCode}
          </span>
        ) : (
          <span className="font-semibold text-ink-3">—</span>
        ),
    },
    {
      key: 'stop',
      header: 'Stop',
      hideOnMobile: true,
      render: (student) => (
        <span className="font-semibold text-ink-2">{student.stopName ?? '—'}</span>
      ),
    },
    {
      key: 'pickup',
      header: 'Pick-up time',
      hideOnMobile: true,
      render: (student) => (
        <span className="font-semibold text-ink-2 whitespace-nowrap">
          {student.pickupTime ? toClockLabel(student.pickupTime) : '—'}
        </span>
      ),
    },
    {
      key: 'drop',
      header: 'Drop time',
      hideOnMobile: true,
      render: (student) => (
        <span className="font-semibold text-ink-2 whitespace-nowrap">
          {student.dropTime ? toClockLabel(student.dropTime) : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (student) => (
        <Badge tone={STUDENT_TRANSPORT_STATUS[student.status].tone} withDot>
          {STUDENT_TRANSPORT_STATUS[student.status].label}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      render: (student) => (
        <RowActions
          subject={student.fullName}
          onOpen={onOpenStudent && (() => onOpenStudent(student))}
          onEdit={onEditStudent && (() => onEditStudent(student))}
          onMore={onStudentMenu && (() => onStudentMenu(student))}
        />
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-4 rounded-3xl border border-line bg-surface p-4 shadow-sm">
      <RegisterToolbar
        title="All students"
        searchPlaceholder="Search students…"
        register={register}
        statusOptions={[
          { value: 'ACTIVE', label: STUDENT_TRANSPORT_STATUS.ACTIVE.label },
          { value: 'NOT_USING', label: STUDENT_TRANSPORT_STATUS.NOT_USING.label },
        ]}
        actions={
          <button
            type="button"
            onClick={() => onExport?.(chosen.length > 0 ? chosen : register.visible)}
            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl bg-accent px-3.5 text-sm font-bold text-white transition-colors hover:bg-accent-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            <Download className="h-4 w-4" aria-hidden />
            {/* Says what is about to leave the building. "Export" alone, with
                eight rows ticked, is a question the reader should not have to
                click to answer. */}
            <span>Export{chosen.length > 0 ? ` (${chosen.length})` : ''}</span>
          </button>
        }
      />

      <div className="-mx-4">
        <Table
          columns={columns}
          rows={register.page}
          rowKey={(student) => student.uuid}
          variant="plain"
          empty={
            <EmptyState
              title={register.query || register.status ? 'No students match' : 'Nobody on transport'}
              description={
                register.query || register.status
                  ? 'Nothing matches that search or filter. Try another name, class or route.'
                  : 'Children appear here once they have been given a route and a stop.'
              }
            />
          }
        />

        {register.visible.length > 0 && (
          <Pagination
            meta={register.meta}
            onPageChange={register.goToPage}
            itemLabel="students"
            showPageNumbers
            limitOptions={ROWS_PER_PAGE}
            onLimitChange={register.setLimit}
          />
        )}
      </div>
    </section>
  );
}
